import { useState, useRef, useMemo, useCallback, useEffect } from "react";

interface UseVirtualScrollOptions {
  itemCount: number;
  itemHeight: number; // Chiều cao cố định cho mỗi item
  containerHeight: number; // Chiều cao của container có thể cuộn
  overscan?: number; // Số lượng item được render thêm bên trên/dưới khung nhìn
  scrollThrottleMs?: number; // Thời gian throttle cho scroll event (ms)
}

interface VirtualItem {
  index: number;
  start: number; // Vị trí Y từ trên xuống
  size: number; // Chiều cao của item
}

export const useVirtualScroll = ({
  itemCount,
  itemHeight,
  containerHeight,
  overscan = 3,
  scrollThrottleMs = 16, // ~60fps
}: UseVirtualScrollOptions) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const lastScrollTimeRef = useRef<number>(0);

  // Tính toán các item nào đang hiển thị
  const virtualItems = useMemo(() => {
    // Edge case: không có item nào
    if (itemCount === 0) {
      return [];
    }

    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(itemCount - 1, Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan);

    const items: VirtualItem[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      items.push({
        index: i,
        start: i * itemHeight,
        size: itemHeight,
      });
    }

    return items;
  }, [scrollTop, itemCount, itemHeight, containerHeight, overscan]);

  // Tổng chiều cao của tất cả các item
  const totalHeight = useMemo(() => itemCount * itemHeight, [itemCount, itemHeight]);

  // Xử lý sự kiện cuộn với throttle và requestAnimationFrame
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const now = Date.now();
      const newScrollTop = e.currentTarget.scrollTop;

      // Đánh dấu đang scroll
      if (!isScrolling) {
        setIsScrolling(true);
      }

      // Clear timeout cũ
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Đặt timeout để đánh dấu kết thúc scroll
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);

      // Throttle scroll updates
      if (now - lastScrollTimeRef.current < scrollThrottleMs) {
        return;
      }

      lastScrollTimeRef.current = now;

      // Cancel animation frame cũ nếu có
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Cập nhật scroll position trong animation frame
      animationFrameRef.current = requestAnimationFrame(() => {
        setScrollTop(newScrollTop);
      });
    },
    [isScrolling, scrollThrottleMs],
  );

  // Cuộn đến một index cụ thể
  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = "auto") => {
      if (scrollElementRef.current && index >= 0 && index < itemCount) {
        scrollElementRef.current.scrollTo({
          top: index * itemHeight,
          behavior,
        });
      }
    },
    [itemHeight, itemCount],
  );

  // Cuộn xuống dưới cùng (hữu ích cho tin nhắn)
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      if (itemCount > 0) {
        scrollToIndex(itemCount - 1, behavior);
      }
    },
    [itemCount, scrollToIndex],
  );

  // Cuộn đến top
  const scrollToTop = useCallback((behavior: ScrollBehavior = "auto") => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTo({
        top: 0,
        behavior,
      });
    }
  }, []);

  // Dọn dẹp khi component unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    virtualItems,
    totalHeight,
    scrollElementRef,
    handleScroll,
    scrollToIndex,
    scrollToBottom,
    scrollToTop,
    isScrolling, // Có thể dùng để tắt animations khi đang scroll
    scrollTop, // Expose scroll position nếu cần
  };
};
